<template>
  <div id="app-container">
    <AppHeader />
    <main class="main-content">
      <router-view />
    </main>
    <AppToast />
    <AppConfirmDialog />
    <footer class="app-footer" v-if="!isAdminRoute">
      <div class="footer-content">
        <div class="footer-brand">
          <p class="footer-name">Attractive Salon &amp; Academy</p>
          <p class="footer-tagline">Beauty. Learning. Confidence.</p>
          <nav class="footer-nav" aria-label="Footer">
            <router-link to="/">Home</router-link>
            <router-link to="/about">About</router-link>
            <router-link to="/services">Services</router-link>
            <router-link to="/book-appointment">Book</router-link>
            <router-link to="/academy-enquiry">Academy</router-link>
          </nav>
        </div>
        <div class="footer-contact">
          <p>
            Near Ayush Hospital, Rail Toly<br />
            Gondia, Maharashtra – 441614, India
          </p>
          <p>
            <a href="tel:+918446607478">+91-844-660-7478</a>
            &nbsp;·&nbsp;
            <a href="mailto:attractiveunisexsalon@gmail.com">attractiveunisexsalon@gmail.com</a>
          </p>
        </div>
      </div>
      <p class="footer-copy">&copy; {{ new Date().getFullYear() }} Attractive Unisex Salon. All rights reserved.</p>
    </footer>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/store/auth.store';
import { mapActions } from 'pinia';
import AppHeader from '@/components/AppHeader.vue';
import AppToast from '@/components/AppToast.vue';
import AppConfirmDialog from '@/components/AppConfirmDialog.vue';

export default defineComponent({
  name: 'App',
  components: {
    AppHeader,
    AppToast,
    AppConfirmDialog,
  },
  setup() {
    const route = useRoute();
    const isAdminRoute = computed(() => route.path.startsWith('/admin'));
    return { isAdminRoute };
  },
  methods: {
    ...mapActions(useAuthStore, ['checkAuth']),
  },
  created() {
    this.checkAuth();
  }
});
</script>

<style>
/* Global styles */
:root {
  --brand-gold: #DAA520;
  --brand-black: #1a1a1a;
  --text-light: #f0f0f0;
  --text-dark: #333333;
  --background-light: #ffffff;
  --background-dark: var(--brand-black);
  --header-height: 80px;
}

body {
  margin: 0;
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-dark);
  background-color: var(--background-light);
  line-height: 1.6;
}

#app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: var(--header-height, 80px);
}

.main-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.main-content:has(.admin-layout) {
  padding: 0;
}

.app-footer {
  background-color: var(--brand-black);
  color: var(--text-light);
  padding: 32px 20px 20px;
  text-align: center;
  font-size: 0.9em;
}

.footer-content {
  max-width: 900px;
  margin: 0 auto 20px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 24px;
  text-align: left;
}

.footer-brand {
  flex: 1;
  min-width: 200px;
}

.footer-name {
  font-size: 1.1em;
  font-weight: 600;
  color: var(--brand-gold);
  margin: 0 0 4px;
}

.footer-tagline {
  margin: 0;
  opacity: 0.8;
  font-size: 0.9em;
}

.footer-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-top: 14px;
}

.footer-nav a {
  color: var(--text-light);
  text-decoration: none;
  font-size: 0.9em;
  opacity: 0.85;
}

.footer-nav a:hover,
.footer-nav a.router-link-active {
  color: var(--brand-gold);
  opacity: 1;
}

.footer-contact {
  flex: 1;
  min-width: 240px;
}

.footer-contact p {
  margin: 0 0 8px;
  line-height: 1.6;
  opacity: 0.9;
}

.footer-contact a {
  color: var(--brand-gold);
  text-decoration: none;
}

.footer-contact a:hover {
  text-decoration: underline;
}

.footer-copy {
  margin: 0;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  opacity: 0.7;
  font-size: 0.85em;
}

@media (max-width: 576px) {
  .footer-content {
    flex-direction: column;
    text-align: center;
  }

  .footer-nav {
    justify-content: center;
  }
}
</style>